// TerrainDebug3D — the `window.lfTerrainDebug3D` deep-inspection hook for the Set15 3D render
// path (Set15 P5 debugging). The TerrainRenderer pushes its LIVE camera + mesh state here each
// `useFrame` body via `terrainDebug3DUpdate`; an external tool (e.g. the agent browser session)
// reads a snapshot via `window.lfTerrainDebug3D.refresh()`.
import type {TerrainQuadtree} from "./TerrainQuadtree.js";

/** Live 3D-render state the hook snapshots. Re-pushed every `useFrame` body. */
export interface TerrainDebug3DState {
	/** The R3F camera (ortho or perspective). */
	camera: unknown;
	/** Is the current camera perspective (3D)? */
	isPerspective: boolean;
	/** Number of meshes created this frame (active, not yet released). */
	activeMeshCount: number;
	/** Total mesh pool size. */
	poolSize: number;
	/** Per-leaf texVersion histogram. */
	evaluatedChunks: number;
	totalChunks: number;
	/** The active terrain quadtree. */
	qt: TerrainQuadtree|null;
	/** The R3F scene (for inspecting object counts). */
	scene: unknown;
	/** worldToScreen at viewport centre (pixels/metre). */
	worldToScreen: number;
	/** cameraView rect. */
	cameraView: {x: number; y: number; width: number; height: number}|null;
	/** eye / camera position. */
	eye: {x: number; y: number; z: number}|null;
}

let state: TerrainDebug3DState = {
	camera: null,
	isPerspective: false,
	activeMeshCount: 0,
	poolSize: 0,
	evaluatedChunks: 0,
	totalChunks: 0,
	qt: null,
	scene: null,
	worldToScreen: 0,
	cameraView: null,
	eye: null,
};

/** Re-point the snapshot source to the LIVE renderer state (call each `useFrame` body). */
export function terrainDebug3DUpdate(s: Partial<TerrainDebug3DState>): void {
	Object.assign(state, s);
}

/** Fresh snapshot of the live 3D render state. */
export function terrainDebug3DRefresh(): any {
	const out: any = {
		isPerspective: state.isPerspective,
		activeMeshCount: state.activeMeshCount,
		poolSize: state.poolSize,
		evaluatedChunks: state.evaluatedChunks,
		totalChunks: state.totalChunks,
		worldToScreen: state.worldToScreen,
		cameraView: state.cameraView,
		eye: state.eye,
	};
	if (state.camera) {
		const c = state.camera as any;
		out.camera = {
			type: c.isPerspectiveCamera ? "perspective" : "ortho",
			position: {x: c.position.x, y: c.position.y, z: c.position.z},
			up: {x: c.up.x, y: c.up.y, z: c.up.z},
			near: c.near, far: c.far,
			fov: c.fov, zoom: c.zoom,
			left: c.left, right: c.right, top: c.top, bottom: c.bottom,
		};
	}
	if (state.scene) {
		const s = state.scene as any;
		const children: any[] = [];
		// Walk the scene graph's immediate children (meshes, groups, lights).
		const walk = (node: any, depth: number): void=>{
			if (depth > 2) return;
			if (node.isMesh) {
				children.push({
					type: "mesh",
					name: node.name,
					visible: node.visible,
					position: {x: node.position.x, y: node.position.y, z: node.position.z},
					materialType: node.material?.type,
					geometryType: node.geometry?.type,
					geoVerts: node.geometry?.attributes?.position?.count,
					scale: {x: node.scale.x, y: node.scale.y, z: node.scale.z},
				});
				return;
			}
			if (node.isGroup || node.isObject3D) {
				for (const ch of (node.children ?? [])) walk(ch, depth + 1);
			}
		};
		for (const ch of (s.children ?? [])) walk(ch, 0);
		out.scene = s;
		out.sceneChildren = children;
		out.sceneMeshCount = children.length;
	}
	return out;
}

declare global {
	interface Window {
		/** Set15 P5 debug hook — deep-inspection snapshot of the live 3D render state.
		 *  Call `window.lfTerrainDebug3D.refresh()` for a fresh read. */
		lfTerrainDebug3D?: {refresh(): any};
	}
}

// Install at module load; re-installed on hot-reload (the renderer re-pushes state each body).
if (typeof window !== "undefined") {
	window.lfTerrainDebug3D = {refresh: terrainDebug3DRefresh};
}
